import { CommonModule } from '@angular/common';
import { AfterViewInit, inject, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, ReactiveFormsModule,Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import moment, { Moment } from 'moment';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { viewEmploymentHistory } from '../../new-rep-application-interfaces/employmenthistory.interface';
import { viewAttachements } from '../../new-rep-application-interfaces/attachements.interface';
import { NewRepApplicationService } from '../../new-rep-application-services/new-rep-application.service';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmPopupComponent } from '../../confirm-popup/confirm-popup.component';
import { SnackbarService } from '../../../../../shared/services/snackbar/snackbar.service';
import { HttpStatusCode } from 'axios';
import { SessionStorageService } from '../../../../../core/services/session-storage/session-storage.service';
import { noWhitespace } from '../../../../../shared/validators/no-whitespace.validator';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'app-employment-history',
  providers: [provideMomentDateAdapter(MY_FORMATS)],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatNativeDateModule,
    CommonModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
  ],
  templateUrl: './employment-history.component.html',
  styleUrl: './employment-history.component.scss'
})
export class EmploymentHistoryComponent implements OnInit {

  @Input() formGroup!: FormGroup;
  @Input() controlName: string = 'employementhistory';
  @Input() employmentHistory!: viewEmploymentHistory[];
  @Input() readonly = signal(false);
  @Output() next = new EventEmitter<string[]>();

  private fb = inject(FormBuilder);
  private readonly matDialog = inject(MatDialog);
  private newRepApplicationService = inject(NewRepApplicationService);
  readonly message = 'This field is required.';
  status = 'Not Started';
  async ngOnInit(): Promise<void> {
    //  Use dynamic controlName (reusable fix)
    if (!this.formGroup.get(this.controlName)) {
      this.formGroup.addControl(
        this.controlName,
        this.fb.array([])
      );
    }

    //  Always show at least one row
    if (this.formArray.length === 0) {
      this.addRow();
    }

    await this.fetchEmploymentHistory();
    this.handleFormStatus();
  }

  fetchEmploymentHistory(): void {
    this.employmentHistory.forEach((item, index) => {
      // Ensure row exists
      if (index !== 0) {
        this.addRow();
      }

      this.formArray.at(index).patchValue({
        startDate: item.startDate,
        endDate: item.endDate,
        email: item.employerContactDetails,
        employer: item.employerName,
        position: item.lastPositionHeld,
        employment: item.natureOfEmployment,
        reason: item.reasonForLeaving,
      });
    });
    const anyFilled = this.formArray.value.some((group: { [s: string]: unknown; } | ArrayLike<unknown>) =>
        Object.values(group).some(
          v => v && v.toString().trim() !== ''
        )
    );

    if (!anyFilled) {
      this.status = 'Not Started';
      this.next.emit([this.status, 'submitted']);

    } else if (this.formGroup.valid) {
      this.status = 'Completed';
      this.next.emit([this.status, 'submitted']);

    } else {
      this.status = 'In Progress';
      this.next.emit([this.status, 'submitted']);
    }
    if (this.readonly() == true) {
      this.formGroup?.disable();
    } else {
      this.formGroup?.enable();
    }
  }

  //  Generic FormArray getter
  get formArray(): FormArray {
    return this.formGroup.get(this.controlName) as FormArray;
  }

  //  Create row
  createRow(): FormGroup {
    return this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      email: ['', [Validators.required,noWhitespace]],
      employer: ['', [Validators.required,noWhitespace]],
      position: ['', [Validators.required,noWhitespace]],
      employment: ['', [Validators.required,noWhitespace]],
      reason: ['', [Validators.required,noWhitespace]]
    }, { validators: this.dateRangeValidator });
  }

  //  Add row
  addRow(): void {
    this.formArray.push(this.createRow());
  }

  //  Remove row (keep at least one row)
  async removeRow(index: number): Promise<void> {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to delete the Employemnt history?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.formArray.length > 1) {
          if (this.employmentHistory[index]?.id != undefined) {
            let id = this.employmentHistory[index]?.id;

            const responseData = await this.newRepApplicationService
              .deletemploymenthistory(id)
              .then((response) => {
                return JSON.parse(response);
              })
              .catch((error) => {
                // console.error(error);
                return error;
              });
          }
          this.formArray.removeAt(index);
        }
      } else {
        // console.log('User cancelled');
      }
    });

  }

  dateRangeValidator(group: FormGroup) {
    const start = group.get('startDate')?.value; // format: "2026-04"
    const end = group.get('endDate')?.value;

    if (!start || !end) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);

    return endDate >= startDate ? null : { invalidDateRange: true };
  }

  setstartDateMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>, index: number) {
    const control = this.formArray.at(index).get('startDate');
    const ctrlValue = moment(control?.value || new Date());
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    control?.setValue(ctrlValue);
    datepicker.close();
  }

  setendDateMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>, index: number) {
    const control = this.formArray.at(index).get('endDate');
    const ctrlValue = moment(control?.value || new Date());
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    control?.setValue(ctrlValue);
    datepicker.close();
  }


  handleFormStatus() {
    if (this.readonly()) return;

    const initialValue = this.formGroup.getRawValue();

    this.formGroup.valueChanges.subscribe(currentValue => {
      const hasChanged =
        JSON.stringify(initialValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'change']);
      }
    });
  }

  //  Submit
  saveAndContinue(): void {
    // Save API call or form validation here
    if (this.formGroup.valid) {
      this.status = 'Completed';

    }
    this.formGroup.markAllAsTouched();

    this.next.emit([this.status, 'submit']);
  }
}
